<script setup lang="ts">
import {
  computed,
  inject,
  onMounted,
  onUnmounted,
  provide,
  ref,
  toRef,
  watch,
} from 'vue';
import {
  ContentsContext,
  Feature,
  FeedType,
  IndustryData,
  PageName,
  SearchData,
} from '@/api/tracking';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import AdpDocumentActionBar from '@/components/adp-document/actions/adp-document-action-bar.vue';
import AdpDocumentBasicInfo from '@/components/common/adp-document/basic-info.vue';
import RelatedArticlesDisplay from '@/components/common/adp-document/related-articles-display.vue';
import TagItem from '@/components/common/atoms/tag.vue';
import CommentList from '@/components/common/comment/comment-list.vue';
import CommentCreateModal from '@/components/modals/comment-create-modal.vue';
import CommentEditModal from '@/components/modals/comment-edit-modal.vue';
import DialogGroupMark from '@/components/modals/dialog-group-mark.vue';
import MarkModal from '@/components/modals/mark-modal.vue';
import {
  AdpDocument,
  AdpDocumentOptionAction,
  AdpDocumentOptionActionName,
  ArticleMarkSource,
  Comment,
  Group,
  isArticle,
  Mention,
} from '@/types';
import { useAdpDocumentActions } from '@/utils/actions/useAdpDocumentActions';
import { getDocTypeDisplayName, isNewArticle } from '@/utils/adpDocument';
import { useAdpDocumentMarkPresentation } from '@/utils/composables/useAdpDocumentMarkPresentation';
import { useAdpDocumentModals } from '@/utils/composables/useAdpDocumentModals';
import {
  copyLinkOptionAction,
  useCopyDocumentLink,
} from '@/utils/composables/useCopyDocumentLink';
import { useRelatedArticles } from '@/utils/composables/useRelatedArticles';
import {
  adpDocumentCardWrapperRefKey,
  visibleAdpCardKey,
} from '@/utils/injectionKeys';

interface Props {
  adpDocument?: AdpDocument;
  comment?: Comment;
  themeId?: number;
  width?: string;
  showComment?: boolean;
  articleExclusionId?: number;
  pageName: PageName;
  feature?: Feature;
  feedType?: FeedType;
  markSource?: ArticleMarkSource;
  rankInWholeFeed?: number;
  searchData?: SearchData;
  isSelected?: boolean;
  enableHover?: boolean;
  showCommentCount?: number;
  group?: Group;
  foldReplies?: boolean;
  caseStudyText?: string;
  trendCompanyText?: string;
  industryData?: IndustryData;
  optionActions?: AdpDocumentOptionAction[];
  showUserAction?: boolean;
  contentsContext?: ContentsContext;
  disableRelatedArticles?: boolean;
  showTagList?: boolean;
  priorityDisplayTags?: string[];
  execution?: string;
  isNarrow?: boolean;
  largeReportThumbnail?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  width: '616px',
  showComment: true,
  isSelected: false,
  enableHover: false,
  showCommentCount: 3,
  foldReplies: true,
  caseStudyText: '',
  trendCompanyText: '',
  optionActions: () => [],
  showUserAction: true,
  disableRelatedArticles: false,
  priorityDisplayTags: () => [],
  isNarrow: false,
  largeReportThumbnail: false,
});

const emit = defineEmits<{
  optionAction: [emitName: AdpDocumentOptionActionName];
  'article-clicked': [adpDocument: AdpDocument];
}>();

const adpDocument = toRef(props, 'adpDocument');

const {
  isPostingComment,
  enableAllianceMedia,
  theme,
  markArticle,
  groupMarkArticle,
  commentArticle,
  editComment,
  deleteComment,
  handleArticleLinkClicked,
  handleArticleContextMenu,
} = useAdpDocumentActions(adpDocument, props);

const {
  actionModal,
  selectedComment,
  showCreateCommentModal,
  showGroupMarkModal,
  showMarkModal,
  showEditCommentModal,
  closeActionModal,
} = useAdpDocumentModals();

const {
  showRelatedArticles,
  relatedArticles,
  relatedQuery,
  requestId,
  getRelatedArticles,
} = useRelatedArticles(adpDocument, {
  disableRelatedArticles: props.disableRelatedArticles,
});

// アクション成功時のみモーダルを閉じる。失敗時は例外が伝播して閉じないため、
// ユーザーは同じ内容で再試行できる。
const markAndClose = async (markProps?: { tags?: string[]; memo?: string }) => {
  await markArticle(markProps);
  closeActionModal();
};

const groupMarkAndClose = async (payload: {
  group: Group;
  tags?: string[];
  memo?: string;
}) => {
  await groupMarkArticle(payload);
  closeActionModal();
};

const commentAndClose = async (payload: {
  group: Group;
  content: string;
  mention?: Mention;
}) => {
  await commentArticle(payload);
  closeActionModal();
};

const editCommentAndClose = async (payload: {
  comment: Comment;
  content: string;
}) => {
  await editComment(payload);
  closeActionModal();
};

const displayTags = computed(() => {
  const tagList = adpDocument.value?.tags ?? [];
  const priorityTags = tagList.filter(tag =>
    props.priorityDisplayTags.includes(tag),
  );

  return [
    ...priorityTags,
    ...tagList.filter(tag => !priorityTags.includes(tag)),
  ];
});

const hitKeywords = computed(() => {
  const hit_keywords = adpDocument.value?.hit_keywords ?? [];
  const synonym_keywords = adpDocument.value?.synonym_keywords ?? [];
  const keywords = [...hit_keywords, ...synonym_keywords].filter(
    keyword => keyword.length > 0,
  );
  return keywords;
});

const articleComments = computed(() => adpDocument.value?.comments ?? []);
const comments = computed(() => {
  if (props.comment) return [props.comment];
  return articleComments.value;
});

const latestComments = computed(() => {
  return comments.value.slice(-props.showCommentCount);
});

const hasComment = computed(() => {
  return props.comment || comments.value.length > 0;
});

const commentsVisible = computed(() => !!hasComment.value && props.showComment);

const isIndustryNews = computed(() => {
  const isEnabled = props.caseStudyText !== '' && props.trendCompanyText !== '';
  return isEnabled;
});

const showNewTag = computed(
  () => isIndustryNews.value && isNewArticle(adpDocument.value),
);

const docTypeDisplayName = computed(() =>
  getDocTypeDisplayName(adpDocument.value),
);

const isAllianceMedia = computed(() => {
  if (!adpDocument.value) return false;
  return (
    'is_alliance_media' in adpDocument.value &&
    adpDocument.value.is_alliance_media
  );
});

const { isMarked, markedUsers, markedGroups, commentedUsers } =
  useAdpDocumentMarkPresentation(adpDocument);

const { copyLink } = useCopyDocumentLink();
const optionActions = computed<AdpDocumentOptionAction[]>(() => [
  copyLinkOptionAction,
  ...props.optionActions,
]);
const handleOptionAction = (emitName: AdpDocumentOptionActionName) => {
  if (emitName === 'copy-link') {
    if (adpDocument.value) copyLink(adpDocument.value);
    return;
  }
  emit('optionAction', emitName);
};

const onClickLink = () => {
  if (!adpDocument.value) return;
  emit('article-clicked', adpDocument.value);
};

const isInactiveTag = (item: string) => {
  if (props.priorityDisplayTags.length === 0) return false;
  return !props.priorityDisplayTags.includes(item);
};

watch(showRelatedArticles, show => {
  if (!adpDocument.value) return;

  const container = document.getElementById(
    `related-articles-${adpDocument.value.id}`,
  );
  if (!container) return;

  const relatedArticles = container.firstChild as HTMLElement;

  const openHeight = `${relatedArticles.scrollHeight}px`;
  const containerFrames = [
    { height: '0', opacity: 0 },
    { height: openHeight, opacity: 1 },
  ];
  const relatedArticlesFrames = [
    { transform: 'translateY(-100%)' },
    { transform: 'translateY(0)' },
  ];
  const options = {
    duration: 600,
    iterations: 1,
    easing: 'ease-in-out',
  };
  container.animate(
    show ? containerFrames : [...containerFrames].reverse(),
    options,
  );

  relatedArticles.animate(
    show ? relatedArticlesFrames : [...relatedArticlesFrames].reverse(),
    options,
  );

  if (show) {
    container.classList.add('open');
    container.style.height = openHeight;
  } else {
    container.classList.remove('open');
    container.style.height = '0';
  }
});

// 親が wrapper ref を provide している場合（follow-list 画面など）のみ、カードの
// 表示・非表示を監視し、子コンポーネント（ツールチップやアクション）を遅延表示する。
const wrapperRef = inject(adpDocumentCardWrapperRefKey, null);
const options = {
  root: wrapperRef?.value,
  rootMargin: '0px',
  threshold: 0,
};
const visibleAdpCard = ref(true);
if (wrapperRef) {
  provide(visibleAdpCardKey, visibleAdpCard);
}

const callback = (entries: IntersectionObserverEntry[]) => {
  entries.forEach(entry => {
    visibleAdpCard.value = entry.isIntersecting;
  });
};
const adpDocumentCardRef = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;
onMounted(() => {
  if (!wrapperRef?.value) return;
  observer = new IntersectionObserver(callback, options);
  if (adpDocumentCardRef.value) {
    observer.observe(adpDocumentCardRef.value);
  }
});
onUnmounted(() => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
});
</script>

<template>
  <div
    class="document-card"
    :style="{ width: width }"
    :class="{
      'document-card__inner': adpDocument && !commentsVisible,
      'document-card-comment__inner': adpDocument && commentsVisible,
      selected: isSelected,
      hovered: enableHover,
    }"
    ref="adpDocumentCardRef"
  >
    <template v-if="articleExclusionId">
      <div class="o-tags">
        <div class="o-not-interested-tag">
          <DgrIcon size="small" name="thumbs-down" />
          <div class="o-text c-text-regular--s">
            この{{ docTypeDisplayName }}に興味がない
          </div>
        </div>
      </div>
    </template>
    <template v-if="displayTags.length !== 0 && showTagList">
      <div class="tag-items">
        <DgrIcon size="small" name="tag" class="tag-icon" :keep-fill="false" />
        <div v-for="item in displayTags" :key="item">
          <TagItem
            class="selected-tag-item"
            :key="item"
            :name="item"
            :is-editing="false"
            :is-small="true"
            :inactive="isInactiveTag(item)"
          >
          </TagItem>
        </div>
      </div>
    </template>

    <VTooltip placement="top" :disabled="hitKeywords.length === 0">
      <div v-if="adpDocument" class="document-info">
        <div class="document-body">
          <AdpDocumentBasicInfo
            :adp-document="adpDocument"
            :trend-company-text="isIndustryNews ? trendCompanyText : undefined"
            :is-new-article="showNewTag"
            :is-narrow="isNarrow"
            :large-report-thumbnail="largeReportThumbnail"
            @article-link-clicked="
              clickedMainTitle => {
                onClickLink();
                handleArticleLinkClicked();
                if (clickedMainTitle) getRelatedArticles();
              }
            "
            @article-link-context-menu="handleArticleContextMenu($event)"
          />
        </div>
      </div>
      <template #popper>
        <span>含むキーワード</span>
        <div v-for="(keyword, keywordIndex) in hitKeywords" :key="keywordIndex">
          ・{{ keyword }}
        </div>
      </template>
    </VTooltip>
    <AdpDocumentActionBar
      v-if="adpDocument && showUserAction"
      class="actions-row"
      :adp-document="adpDocument"
      :is-marked="isMarked"
      :marked-users="markedUsers"
      :marked-groups="markedGroups"
      :commented-users="commentedUsers"
      :option-actions="optionActions"
      :disabled="!enableAllianceMedia && isAllianceMedia"
      :is-narrow="isNarrow"
      :mark-handler="markArticle"
      @option-action="handleOptionAction"
      @handle-popup-menu-click="showMarkModal"
      @handle-comment-click="showCreateCommentModal"
      @handle-group-mark-click="showGroupMarkModal"
    />
    <div
      class="spacing-12"
      v-else-if="adpDocument?.doc_type === 'report'"
    ></div>
    <div
      v-if="
        isArticle(adpDocument) &&
        adpDocument.related_articles_available &&
        relatedArticles.length > 0 &&
        relatedQuery
      "
      :id="`related-articles-${adpDocument.id}`"
      class="related-articles-container"
    >
      <RelatedArticlesDisplay
        class="related-articles"
        :page-name="pageName"
        :related-articles="relatedArticles"
        :related-query="relatedQuery"
        :request-id="requestId"
        @close="showRelatedArticles = false"
      />
    </div>
    <div
      class="o-divider"
      v-if="
        adpDocument &&
        showComment &&
        latestComments.length > 0 &&
        !isIndustryNews
      "
    ></div>
    <CommentList
      v-if="hasComment && showComment"
      :adp-document="adpDocument"
      :comments="latestComments"
      :feature="feature"
      :page-name="pageName"
      :fold-replies="foldReplies"
      :is-narrow="isNarrow"
      :show-avatar="!isNarrow"
      @click-comment="showCreateCommentModal"
      @edit-comment="showEditCommentModal"
      @delete-comment="deleteComment"
    />
    <MarkModal
      v-if="actionModal === 'mark' && adpDocument"
      :adp-document="adpDocument"
      :theme="theme"
      :page-name="pageName"
      :feature="feature"
      @submit="markAndClose"
      @close="closeActionModal"
    ></MarkModal>
    <DialogGroupMark
      v-else-if="actionModal === 'group_mark' && adpDocument"
      :adp-document="adpDocument"
      :theme="theme"
      :group="group"
      :page-name="pageName"
      :feature="feature"
      @submit="groupMarkAndClose"
      @close="closeActionModal"
    ></DialogGroupMark>
    <CommentCreateModal
      v-else-if="actionModal === 'comment' && adpDocument"
      :adp-document="adpDocument"
      :group="group"
      :theme="theme"
      :page-name="pageName"
      :feature="feature"
      :is-posting-comment="isPostingComment"
      @submit="commentAndClose"
      @close="closeActionModal"
    ></CommentCreateModal>
    <CommentEditModal
      v-else-if="actionModal === 'commentEdit' && selectedComment"
      :comment="selectedComment"
      v-model:is-posting-comment="isPostingComment"
      @submit="editCommentAndClose"
      @close="closeActionModal"
    ></CommentEditModal>
  </div>
</template>

<style scoped lang="scss">
div.document-card {
  .tag-items {
    margin-bottom: 10px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    row-gap: 8px;
    .selected-tag-item {
      margin-right: 4px;
    }

    .tag-icon {
      margin-right: 4px;
      color: $color-gray600;
    }
  }

  width: 'auto';
  background-color: white;
  border: 1px solid $color-gray400;
  box-sizing: border-box;
  border-radius: 4px;

  &.selected {
    border-color: $color-green600;
  }
  &.hovered {
    &:hover {
      border-color: $color-gray800;
    }
  }

  .o-tags {
    display: flex;
    flex-direction: row;
    margin-bottom: 4px;
    .o-theme-tag {
      margin: -8px 0 2px 0;
      border-radius: 4px;
      padding: 1px;
      color: $color-gray1000;
      &:before {
        content: '# ';
      }
      &:not(:last-child) {
        &:after {
          content: ', ';
        }
      }
    }
    a .o-theme-tag {
      cursor: pointer;
      &:hover {
        text-decoration: underline;
      }
    }
  }
  .o-not-interested-tag {
    display: flex;
    align-items: center;
    .o-text {
      margin-left: 2px;
    }
  }
  .document-info {
    display: flex;
    flex-direction: row;

    .document-body {
      display: flex;
      justify-content: space-between;
      width: 100%;
      align-items: center;
      gap: 16px;
    }
  }
  button.o-comment-count-label {
    height: auto;
    background-color: transparent;
    border: none;
    cursor: pointer;
    appearance: none;
    padding: 4px 0;
    color: $color-green600;
    width: fit-content;
    margin-top: 8px;
    &:hover {
      cursor: pointer;
      text-decoration: underline;
    }
    &:focus {
      outline: 0;
    }
  }

  .related-articles-container {
    overflow: hidden;
    will-change: contents;
    height: 0;
    opacity: 0;

    .related-articles {
      transform: translateY(-100%);
    }

    &.open {
      opacity: 1;
      .related-articles {
        transform: translateY(0);
      }
    }
  }
  .o-divider {
    width: auto;
    height: 1px;
    background: $color-gray400;
    margin: 4px 0;
  }

  .actions-row {
    padding-top: 8px;
  }
}
.document-card__inner {
  padding: 16px 16px 4px 16px;
}
.document-card-comment__inner {
  padding: 16px;
}
</style>
